// src/components/layouts/ClientLayout.tsx - FIXED with underline bar
import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, Search, User, Users, PlusSquare, ShoppingBag, ArrowRight, 
  MessageCircle, Flame, Bell, Plus, Instagram, Facebook, Loader2 
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../contexts/CartContext'; 

// 🟢 FIREBASE IMPORTS FOR NEWSLETTER
import { db } from '../../services/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// 🟢 CUSTOM TIKTOK ICON (Matches Lucide style)
const TikTokIcon = ({ size = 20, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

export const ClientLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const { cartItems } = useCart();
  const cartItemCount = cartItems?.reduce((total: number, item: any) => total + (item.quantity || 1), 0) || 0;

  const unreadNotifications = 0; 
  const unreadMessages = 0;

  // 🟢 TOP NAVIGATION TAB STATE
  const [activeTopTab, setActiveTopTab] = useState('brands');

  // Set active tab based on current path
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') setActiveTopTab('brands');
    else if (path === '/jewellery') setActiveTopTab('jewellery');
    else if (path === '/thrifts') setActiveTopTab('thrift');
    else if (path === '/designer') setActiveTopTab('designer');
    else if (path === '/games') setActiveTopTab('games');
  }, [location.pathname]);

  // 🟢 HANDLE TAB CLICK
  const handleTabClick = (tab: string) => {
    if (tab === 'brands') {
      navigate('/');
      return;
    }
    if (tab === 'jewellery') {
      navigate('/jewellery');
      return;
    }
    if (tab === 'thrift') {
      navigate('/thrifts');
      return;
    }
    if (tab === 'designer') {
      navigate('/designer');
      return;
    }
    if (tab === 'games') {
      navigate('/games');
      return;
    }
    setActiveTopTab(tab);
  };

  // 🟢 NEWSLETTER STATE & LOGIC
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    
    setIsSubscribing(true);
    try {
      await addDoc(collection(db, 'newsletters'), {
        email: email.trim(),
        createdAt: serverTimestamp()
      });
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription failed:', error);
    } finally {
      setIsSubscribing(false);
    }
  };

  // 🟢 FOOTER LINKS ROUTING
  const footerLinks = [
    { name: 'Sell', url: '/sell', external: false },
    { name: 'Support', url: 'https://scoophype.co.za/support', external: true },
    { name: 'Terms', url: 'https://scoophype.co.za/Terms', external: true },
    { name: 'Privacy', url: 'https://scoophype.co.za/privacy', external: true }
  ];

  return (
    <div className="flex flex-col min-h-screen w-full bg-black text-white font-sans selection:bg-white selection:text-black">

      {/* =========================================================================
          🖥️ DESKTOP HEADER (Visible ONLY on LG+) - LOGO ABOVE NAV WITH UNDERLINE
      ========================================================================= */}
      <header className="hidden lg:flex fixed top-0 left-0 right-0 z-50 bg-black border-b border-zinc-800 flex-col">
        {/* Top Bar - Free Shipping */}
        <div className="flex justify-between items-center py-2 px-8 border-b border-zinc-800 text-[10px] uppercase tracking-widest font-bold text-zinc-500">
          <span>Masterartsconnect</span>
          <span>Free Shipping on Orders over R1000 On Us</span>
        </div>

        {/* Logo Row - Centered */}
        <div className="flex items-center justify-center h-16 px-8 border-b border-zinc-800">
          <h1 className="text-4xl font-black italic tracking-tighter cursor-pointer" onClick={() => window.location.href='/'}>
            SCOOP<span className="text-zinc-800">.</span>
          </h1>
        </div>

        {/* Navigation & Icons Row - WITH UNDERLINE BAR */}
        <div className="flex items-center justify-between h-14 px-8 relative">
          {/* 🟢 NAVIGATION - Left side */}
          <nav className="flex gap-4 xl:gap-6 items-center h-full">
            {['Brands', 'Jewellery', 'Thrift', 'Designer', 'Collections', 'Community'].map((link) => {
              // Map display names to route paths
              let path = '/';
              if (link === 'Brands') path = '/';
              else if (link === 'Jewellery') path = '/jewellery';
              else if (link === 'Thrift') path = '/thrifts';
              else if (link === 'Designer') path = '/designer';
              else if (link === 'Games') path = '/games';
              else if (link === 'Collections') path = '/collections';
              else if (link === 'Community') path = '/community';
              
              return (
                <NavLink 
                  key={link} 
                  to={path}
                  className={({isActive}) => `
                    relative h-full flex items-center text-xs font-black uppercase tracking-[0.2em] hover:text-white transition-colors whitespace-nowrap
                    ${isActive ? 'text-white' : 'text-zinc-500'}
                  `}
                >
                  {link}
                  {/* 🟢 UNDERLINE INDICATOR */}
                  <span 
                    className={`
                      absolute bottom-0 left-0 h-[2px] bg-white transition-all duration-300 ease-out
                      ${({isActive}) => isActive ? 'w-full' : 'w-0'}
                    `}
                  />
                </NavLink>
              );
            })}
          </nav>

          {/* 🟢 RIGHT ICONS */}
          <div className="flex items-center gap-4 xl:gap-6 flex-shrink-0">
            <NavLink to="/search" className="text-zinc-500 hover:text-white transition-colors" title="Search">
              <Search size={18} />
            </NavLink>
            
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-messages'))} className="text-zinc-500 hover:text-white transition-colors relative" title="Messages">
              <MessageCircle size={18} />
              {unreadMessages > 0 && <span className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full"></span>}
            </button>

            <button onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))} className="text-zinc-500 hover:text-white transition-colors relative" title="Notifications">
              <Bell size={18} />
              {unreadNotifications > 0 && <span className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full"></span>}
            </button>

            <button onClick={() => window.dispatchEvent(new CustomEvent('open-creation-menu'))} className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors" title="Create Post">
              <PlusSquare size={18} />
            </button>

            <NavLink to="/profile" className="text-xs font-black uppercase tracking-widest text-zinc-500 hover:text-white transition-colors whitespace-nowrap">
              {user ? `Account (${profile?.cred || 0})` : 'Login'}
            </NavLink>
            
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-cart'))} className={`flex items-center gap-2 transition-colors ${cartItemCount > 0 ? 'text-white' : 'text-zinc-500 hover:text-white'}`}>
              <ShoppingBag size={18} />
              <span className="text-xs font-black">({cartItemCount})</span>
            </button>
          </div>
        </div>
      </header>

      {/* =========================================================================
          📱 MOBILE HEADER & TABS (Visible ONLY on < LG) - FIXED NO SCROLLING
      ========================================================================= */}
      <header className="lg:hidden sticky top-0 z-40 bg-black/95 backdrop-blur-xl border-b border-zinc-800">
        {/* Top Row - SCOOP Logo and Icons */}
        <div className="flex items-center justify-between px-4 py-4">
          <h1 className="text-2xl font-black italic tracking-tighter">SCOOP<span className="text-zinc-800">.</span></h1>
          
          <div className="flex gap-5 items-center text-zinc-300">
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-messages'))} className="hover:text-white transition-colors relative">
              <MessageCircle size={24} />
              {unreadMessages > 0 && <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>}
            </button>
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))} className="hover:text-white transition-colors relative">
              <Bell size={24} />
              {unreadNotifications > 0 && <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>}
            </button>
            
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-cart'))} className="hover:text-white transition-colors relative">
              <ShoppingBag size={24} />
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-white text-black text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* 🟢 FIXED TABS - Brands | Jewellery | Thrift | Games | Designer (NO SCROLLING) */}
        <div className="flex justify-between items-center px-4 pb-1 border-b border-zinc-800/50">
          {['brands', 'jewellery', 'thrift', 'designer'].map((tab) => {
            const isActive = activeTopTab === tab;
            
            // Capitalize first letter for display
            const displayName = tab.charAt(0).toUpperCase() + tab.slice(1);
            
            return (
              <button
                key={tab}
                data-tab={tab}
                onClick={() => handleTabClick(tab)}
                className={`
                  relative flex-1 py-2 text-center text-xs font-black uppercase tracking-wider transition-all duration-200
                  ${isActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'}
                `}
              >
                {displayName}
                {/* 🟢 UNDERLINE INDICATOR */}
                <span 
                  className={`
                    absolute bottom-0 left-0 h-[2px] bg-white transition-all duration-300 ease-out
                    ${isActive ? 'w-full' : 'w-0'}
                  `}
                />
              </button>
            );
          })}
        </div>
      </header>

      {/* =========================================================================
          🌍 MAIN CONTENT AREA
      ========================================================================= */}
      <main className="flex-1 lg:pt-[130px] pt-[112px] pb-24 lg:pb-10 px-0 w-full max-w-[1920px] mx-auto overflow-x-hidden">
         <Outlet />
      </main>

      {/* =========================================================================
          📱 MOBILE PILL NAV (Visible ONLY on < LG)
      ========================================================================= */}
      <div className="lg:hidden fixed bottom-6 left-0 right-0 flex justify-center z-50 pointer-events-none">
          <nav className="bg-black/90 backdrop-blur-xl border border-zinc-800 rounded-full px-6 py-3 flex gap-8 shadow-2xl pointer-events-auto items-center">
            
            <NavLink to="/" className={({ isActive }) => `transition-colors ${isActive ? 'text-white' : 'text-zinc-600 hover:text-white'}`}>
              <Home size={24} />
            </NavLink>

            <NavLink to="/search" className={({ isActive }) => `transition-colors ${isActive ? 'text-white' : 'text-zinc-600 hover:text-white'}`}>
              <Search size={24} />
            </NavLink>

            <button 
              onClick={() => window.dispatchEvent(new CustomEvent('open-creation-menu'))}
              className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:bg-zinc-200 transition-all active:scale-95 shadow-md"
            >
                <Plus size={24} strokeWidth={3} />
            </button>

            <NavLink to="/community" className={({ isActive }) => `transition-colors ${isActive ? 'text-white' : 'text-zinc-600 hover:text-white'}`}>
              <Users size={24} />
            </NavLink>

            <NavLink to="/profile" className={({ isActive }) => `transition-colors ${isActive ? 'text-white' : 'text-zinc-600 hover:text-white'}`}>
              <User size={24} />
            </NavLink>

          </nav>
      </div>
      
      {/* =========================================================================
          🖥️ DESKTOP FOOTER
      ========================================================================= */}
      <footer className="hidden lg:block border-t border-zinc-800 py-16 px-8 bg-black mt-20">
         
         <div className="grid grid-cols-4 gap-12 mb-16">
            <div>
               <h2 className="text-6xl font-black italic tracking-tighter mb-6 text-white">SCOOP<span className="text-zinc-800">.</span></h2>
               <p className="text-zinc-500 text-xs font-mono uppercase tracking-widest leading-relaxed">Create, Collaborate, Dominate</p>
            </div>
            
            {/* 🟢 FULLY FUNCTIONAL NEWSLETTER FORM */}
            <div className="col-span-2 flex flex-col justify-end">
               <div className="border-b border-zinc-800 pb-4 mb-4">
                  <span className="text-xs font-black uppercase tracking-[0.2em] text-zinc-600">Newsletter</span>
               </div>
               <form onSubmit={handleSubscribe} className="flex justify-between items-center group w-full">
                  <input 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubscribing}
                    required
                    placeholder="ENTER YOUR EMAIL" 
                    className="bg-transparent text-2xl font-black uppercase placeholder:text-zinc-800 focus:outline-none w-full tracking-widest text-white disabled:opacity-50" 
                  />
                  <button 
                    type="submit" 
                    disabled={isSubscribing || !email}
                    className="text-zinc-800 group-hover:text-white hover:text-white transition-all cursor-pointer disabled:opacity-50 ml-4"
                  >
                     {isSubscribing ? <Loader2 size={32} className="animate-spin" /> : <ArrowRight size={32} />}
                  </button>
               </form>
            </div>
         </div>

         {/* 🟢 BOTTOM ROW: COPYRIGHT, LINKS & SOCIALS */}
         <div className="flex items-center justify-between pt-8 border-t border-zinc-800">
             
             {/* Left: Copyright & Standard Links */}
             <div className="flex items-center gap-8 flex-wrap">
                 <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600 whitespace-nowrap">
                     © {new Date().getFullYear()} SCOOP. All Rights Reserved. By Masterartsconnect
                 </span>

                 {/* Dynamic Routing for internal vs external links */}
                 {footerLinks.map(link => (
                     link.external ? (
                         <a 
                            key={link.name} 
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors whitespace-nowrap"
                         >
                             {link.name}
                         </a>
                     ) : (
                         <NavLink 
                            key={link.name} 
                            to={link.url} 
                            className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors whitespace-nowrap"
                         >
                             {link.name}
                         </NavLink>
                     )
                 ))}
             </div>

             {/* Right: Social Media */}
             <div className="flex items-center gap-6 flex-shrink-0">
                 <a href="https://instagram.com/scoophype" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-white transition-colors" aria-label="Instagram">
                    <Instagram size={20} />
                 </a>
                 <a href="https://tiktok.com/@scoophype" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-white transition-colors" aria-label="TikTok">
                    <TikTokIcon size={20} />
                 </a>
                 <a href="https://facebook.com/scoophype" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-white transition-colors" aria-label="Facebook">
                    <Facebook size={20} />
                 </a>
             </div>
         </div>

      </footer>

    </div>
  );
};